import type { GameState, ProductionBuildingKind } from "@dailywars/core/engine";
import {
  BUILDABLE_BY_BUILDING,
  bfsReachable,
  buildingAt,
  canBuildUnitKind,
  canMoveAndAttackSameTurn,
  coordEq,
  isAntiAirBuildBlocked,
  isCapturable,
  isProductionBuilding,
  isTransport,
  unitAt,
  unitCanAttack,
  UNIT_DEFS,
} from "@dailywars/core/engine";
import type { Coord, Unit, UnitKind } from "@dailywars/core/engine";

import type { BuildOptionDisabledReason } from "#/components/game/productionBuildMenuTypes";
import { cancelCaptureResetConfirmation } from "#/game/selection/captureResetConfirmation";
import {
  computeAttackOptions,
  computeJoinOptions,
  computeLoadOptions,
} from "#/game/selection/options";
import type { SelectionState } from "#/game/selection/types";
import { undoLastMove } from "#/game/store/gameMutations/undo";
import type { StoreState } from "#/game/store/types";

export function buildOptionsFor({
  state,
  buildingKind,
}: {
  state: GameState;
  buildingKind: ProductionBuildingKind;
}): Array<{
  kind: UnitKind;
  cost: number;
  disabled: boolean;
  disabledReason: BuildOptionDisabledReason | null;
}> {
  const player = state.currentPlayer;
  const funds = state.funds[player];
  return BUILDABLE_BY_BUILDING[buildingKind]
    .map((kind) => {
      const cost = UNIT_DEFS[kind].cost;
      const noAirports = isAntiAirBuildBlocked({ state, kind, player });
      const atCapacity = !canBuildUnitKind({ state, player, unitKind: kind });
      const insufficientFunds = cost > funds;
      const disabled = noAirports || atCapacity || insufficientFunds;
      const disabledReason: BuildOptionDisabledReason | null = disabled
        ? noAirports
          ? "no_airports"
          : atCapacity
            ? "capacity_reached"
            : "insufficient_funds"
        : null;
      return {
        kind,
        cost,
        disabled,
        disabledReason,
      };
    })
    .sort((a, b) => a.cost - b.cost);
}

export function canInspectOwnUnitAttackRange(state: GameState, unit: Unit | undefined): boolean {
  if (!unit) return false;
  if (unit.owner !== state.currentPlayer) return false;
  if (unit.owner !== "p1") return false;
  if (!unit.hasMoved) return false;
  if (!unitCanAttack(unit)) return false;
  return !isOwnSelectableUnit(state, unit);
}

export function isOwnSelectableUnit(state: GameState, unit: Unit | undefined): unit is Unit {
  if (!unit) return false;
  if (unit.owner !== state.currentPlayer) return false;
  if (unit.owner !== "p1") return false;
  if (unit.hasMoved && unit.hasAttacked) return false;
  if (
    unit.hasMoved &&
    !(isTransport(unit.kind) && (unit.cargo !== undefined || unit.unloadReady === true))
  ) {
    return false;
  }
  return true;
}

export function buildUnitSelection(state: GameState, unit: Unit): SelectionState {
  return {
    mode: "unit",
    unitId: unit.id,
    reachable: bfsReachable(state, unit),
    attackOptions: computeAttackOptions({ state, unit, fromPos: unit.pos }),
    joinOptions: computeJoinOptions({ state, unit, fromPos: unit.pos }),
    loadOptions: computeLoadOptions({ state, unit, fromPos: unit.pos }),
  };
}

export function canAttackFromMovePreviewDestination({
  unit,
  destination,
}: {
  unit: Pick<Unit, "kind" | "pos">;
  destination: Coord;
}): boolean {
  return canMoveAndAttackSameTurn(unit.kind) || coordEq(destination, unit.pos);
}

export function buildMovePreview({
  state,
  unit,
  reachable,
  destination,
  path,
}: {
  state: GameState;
  unit: Unit;
  reachable: Coord[];
  destination: Coord;
  path: Coord[];
}): Extract<SelectionState, { mode: "move-preview" }> {
  const occupied = unitAt(state, destination);
  const destinationHasAlliedUnit =
    occupied !== undefined && occupied.owner === unit.owner && occupied.id !== unit.id;

  return {
    mode: "move-preview",
    unitId: unit.id,
    reachable,
    destination,
    path,
    attackOptions:
      canAttackFromMovePreviewDestination({ unit, destination }) && !destinationHasAlliedUnit
        ? computeAttackOptions({ state, unit, fromPos: destination })
        : [],
    joinOptions: computeJoinOptions({ state, unit, fromPos: unit.pos }),
    loadOptions: computeLoadOptions({ state, unit, fromPos: unit.pos }),
  };
}

export function cancelConfirmAttack(state: StoreState): StoreState {
  const sel = state.selection;
  if (sel.mode !== "confirm-attack") return state;
  if (state.animation || state.isSubmitting) return state;
  if (state.localState.currentPlayer !== "p1") return state;
  if (state.localState.status !== "in_progress") return state;

  const unit = state.localState.units.find((u) => u.id === sel.unitId);
  if (!unit) return { ...state, selection: { mode: "none" }, previewAnimation: null };

  if (sel.path.length === 0) {
    return {
      ...state,
      selection: buildUnitSelection(state.localState, unit),
      previewAnimation: null,
    };
  }

  const destination = sel.path[sel.path.length - 1]!;
  const path = sel.path;
  return {
    ...state,
    selection: buildMovePreview({
      state: state.localState,
      unit,
      reachable: bfsReachable(state.localState, unit),
      destination,
      path,
    }),
    previewAnimation: null,
  };
}

export function cancelSelection(state: StoreState): StoreState {
  const sel = state.selection;
  if (sel.mode === "none") return state;
  if (sel.mode === "confirm-capture-reset") {
    return cancelCaptureResetConfirmation(state);
  }
  if (sel.mode === "transport-unload") {
    return undoLastMove(state);
  }
  return {
    ...state,
    selection: { mode: "none" },
    previewAnimation: null,
    animation: null,
    animationQueue: [],
  };
}

export function canOpenBuildMenu(state: GameState, tile: Coord): boolean {
  const buildingTile = buildingAt(state, tile);
  if (!buildingTile || !isProductionBuilding(buildingTile.kind) || buildingTile.owner !== "p1")
    return false;
  if (unitAt(state, tile)) return false;
  return true;
}

export function canCaptureAt({
  state,
  unit,
  pos,
}: {
  state: GameState;
  unit: Unit;
  pos: Coord;
}): boolean {
  if (unit.hasAttacked) return false;
  if (!UNIT_DEFS[unit.kind].canCapture) return false;
  const tile = buildingAt(state, pos);
  if (!tile) return false;
  return isCapturable(tile, unit.owner);
}

export function isJoinTarget(selection: SelectionState, unitId: string): boolean {
  if (selection.mode !== "unit" && selection.mode !== "move-preview") return false;
  return selection.joinOptions.some((o) => o.targetId === unitId);
}

export function isLoadTarget(selection: SelectionState, unitId: string): boolean {
  if (selection.mode !== "unit" && selection.mode !== "move-preview") return false;
  return selection.loadOptions.some((o) => o.targetId === unitId);
}
